import { z } from 'zod'
import {
  DiditOutcomeSchema,
  DistrictStatusSchema,
  ElectionStatusSchema,
  GenderSchema,
  IneligibilityReasonSchema,
  MemberStatusSchema,
  VoteStatusSchema,
} from '../enums'
import { ScheduleSchema } from '../domain/election-config'
import {
  BigIntStringSchema,
  CountryIso3Schema,
  EmailSchema,
  EthAddressSchema,
  Hex32Schema,
  HexSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  MemberIdSchema,
  PhoneSchema,
  ProcessIdSchema,
  SlugSchema,
  UuidSchema,
} from '../primitives'
import { ContactProofSchema } from './contact'
import { GpsReadingSchema } from './kyc'
import { VoteRecordSchema } from './members'

/**
 * Member portal: `/api/o/:orgSlug/*` (organization slug; drafts answer 404).
 * - Before the account exists, calls are authorized by the registration token (header `x-registration-token`,
 *   returned once by /join and kept in localStorage under `de:reg:${orgSlug}`).
 * - After account creation / login, the `member_session` httpOnly cookie (scoped to that organization) is used.
 * - Voting routes add `/elections/:electionSlug` (the voting slug, unique within the organization).
 */

/* ================================================================== join (registration) */

/*
 * POST /api/o/:orgSlug/join → 201 JoinResponse. 409 duplicate_email | duplicate_national_id | duplicate_phone;
 * 409 member_removed (this person was removed: only a re-admission restores them); 400 requirements_not_met;
 * 409 registration_closed (organization not published); 409 identity_final_decision; 400 contact_not_verified
 * (missing, expired or mismatching `emailProof` / `phoneProof`, see api/contact.ts).
 * Header `x-didit-sandbox-scenario` (automation) applies to sandbox-app organizations only.
 *
 * PLAN v4: the fields asked depend on the organization's requirements (`joinFields()` in api/public.ts). The identity
 * fields are optional HERE; the server answers 400 validation_error (details.fields) when the organization needs one
 * that is missing: dateOfBirth (document or age limits), nationalId + nationality (document), locality (districts).
 */
export const JoinRequestSchema = z.object({
  fullName: z.string().trim().min(2, 'Full name is required').max(200),
  /** Required with the document check or age limits (document DOB is authoritative when there is a document). */
  dateOfBirth: IsoDateSchema.optional(),
  /** Required with the document check (dedupe key `nid:` and Didit expected details). */
  nationalId: z.string().trim().min(3, 'National ID number is required').max(40).optional(),
  /** Mandatory, unique per organization; confirmed with a code before joining (`emailProof`). */
  email: EmailSchema,
  /** Required / optional / not collected depending on requirements.contact.phone; confirmed with a code (`phoneProof`). */
  phone: PhoneSchema.optional(),
  /** Proof from POST /contact/email/check for this exact email (kept optional ⇒ explicit 400 contact_not_verified). */
  emailProof: ContactProofSchema.optional(),
  /** Proof from POST /contact/phone/check for this exact phone (required whenever `phone` is sent). */
  phoneProof: ContactProofSchema.optional(),
  /** From the districting list when the organization has one (required then), free text otherwise. */
  locality: z.string().trim().min(1, 'Locality is required').max(200).optional(),
  /** Declared nationality (ISO3): required with the document check (ID number hash country, Didit expected details). */
  nationality: CountryIso3Schema.optional(),
  /** Statistics only. */
  gender: GenderSchema.optional(),
  /** Explicit consent to identity verification and data processing. */
  consent: z.literal(true, { error: 'You must accept to continue' }),
  /** When the organization collects location or accepts GPS residence evidence (browser asks for permission). */
  gps: GpsReadingSchema.optional(),
  /**
   * Test organizations only (PLAN v4 R19): where the member pretends to live, relative to the residence areas. The
   * server derives SYNTHETIC residence evidence (proof of address / document address point, labeled synthetic) from
   * the area geometry. Ignored for live organizations.
   */
  testLocation: z.enum(['inside', 'near', 'outside']).optional(),
})
export type JoinRequest = z.infer<typeof JoinRequestSchema>

export const KycSessionLinkSchema = z.object({
  sessionId: z.string(),
  /** Didit hosted verification URL — redirect the browser here. */
  url: z.string(),
})
export type KycSessionLink = z.infer<typeof KycSessionLinkSchema>

export const JoinResponseSchema = z.object({
  memberId: MemberIdSchema,
  /** Store in localStorage (key `de:reg:${orgSlug}`); send as `x-registration-token`. Shown only once. */
  registrationToken: z.string().min(32),
  status: MemberStatusSchema,
  /**
   * Didit hosted verification to redirect to; null when the organization needs no Didit session (basic / residence
   * by GPS only, PLAN v4 §2): the status is then already decided (approved / in_review / declined_final).
   */
  kyc: KycSessionLinkSchema.nullable(),
})
export type JoinResponse = z.infer<typeof JoinResponseSchema>

/*
 * GET /api/o/:orgSlug/registration (header x-registration-token) → RegistrationStatusResponse.
 * Every token route: 401 invalid_registration_token | registration_token_expired (30 days after the registration
 * last changed); 403 registration_completed once the account exists (log in instead).
 */
export const RegistrationStatusSchema = z.object({
  memberId: MemberIdSchema,
  status: MemberStatusSchema,
  fullName: z.string(),
  email: z.string().nullable(),
  /** Null when the organization does not ask for it (PLAN v4). */
  locality: z.string().nullable(),
  district: z.string().nullable(),
  districtStatus: DistrictStatusSchema,
  /** Latest KYC attempt (null before the first session is created). */
  kyc: z
    .object({
      sessionId: z.string(),
      status: z.string(),
      outcome: DiditOutcomeSchema,
      /** Resume URL while the session is still in progress. */
      url: z.string().nullable(),
      attempt: z.number().int().min(1),
    })
    .nullable(),
  attemptsUsed: z.number().int().min(0),
  maxAttempts: z.number().int().min(1),
  /** True when status = retry_allowed and attempts remain. */
  canRetry: z.boolean(),
  /** True when status = approved (next: create the account). */
  canCreateAccount: z.boolean(),
  accountCreated: z.boolean(),
  /** The organizer's note to the member (reject / "request more info"), null otherwise. Shown verbatim. */
  message: z.string().nullable(),
})
export type RegistrationStatus = z.infer<typeof RegistrationStatusSchema>
export const RegistrationStatusResponseSchema = z.object({ registration: RegistrationStatusSchema })
export type RegistrationStatusResponse = z.infer<typeof RegistrationStatusResponseSchema>

/* POST /api/o/:orgSlug/kyc/session (token) → KycSessionLink. 409 kyc_retry_not_allowed | kyc_attempts_exhausted. */
export const KycRetryResponseSchema = KycSessionLinkSchema
export type KycRetryResponse = z.infer<typeof KycRetryResponseSchema>

/* ================================================================== account / wallet */

/**
 * ethers v6 JSON keystore (`wallet.encrypt(password)` / `encryptKeystoreJson`, scrypt) as a string.
 * The server stores it opaque and returns it on /me; it can never decrypt it.
 */
export const KeystoreJsonSchema = z
  .string()
  .min(100)
  .max(20_000)
  .refine((s) => {
    try {
      const j = JSON.parse(s) as { crypto?: unknown; Crypto?: unknown; address?: unknown }
      return typeof j === 'object' && j !== null && (j.crypto !== undefined || j.Crypto !== undefined)
    } catch {
      return false
    }
  }, 'Invalid keystore JSON')

/**
 * Client-side auth verifier: PBKDF2-SHA256(password, `davinci-elections:${organizationId}:${normalizedEmail}`,
 * 200 000 iterations, 32 bytes) as 0x-hex. ONE account per organization. The raw password never leaves the browser.
 */
export const AUTH_VERIFIER_ITERATIONS = 200_000
export const authVerifierSalt = (organizationId: string, normalizedEmail: string) =>
  `davinci-elections:${organizationId}:${normalizedEmail}`

/* POST /api/o/:orgSlug/account (token; member must be `approved`) → MemberMeResponse + sets member_session. 409 account_exists. */
export const CreateAccountRequestSchema = z.object({
  address: EthAddressSchema,
  keystore: KeystoreJsonSchema,
  authVerifier: Hex32Schema,
})
export type CreateAccountRequest = z.infer<typeof CreateAccountRequestSchema>

/*
 * POST /api/o/:orgSlug/login → MemberMeResponse + cookie. 401 invalid_credentials for a wrong password or an unknown
 * email (no enumeration). With the CORRECT password: 403 member_removed (removed by the organizer) / 403 member_reset
 * (membership reset by the organizer or by a rule change: register again). POST /api/o/:orgSlug/logout → OkResponse.
 */
export const MemberLoginRequestSchema = z.object({
  email: EmailSchema,
  authVerifier: Hex32Schema,
})
export type MemberLoginRequest = z.infer<typeof MemberLoginRequestSchema>

/* GET /api/o/:orgSlug/me (cookie) → MemberMeResponse */
export const MemberMeSchema = z.object({
  memberId: MemberIdSchema,
  organizationId: UuidSchema,
  status: MemberStatusSchema,
  fullName: z.string(),
  email: z.string().nullable(),
  walletAddress: EthAddressSchema,
  /** Encrypted wallet to decrypt in the browser with the password. */
  keystore: z.string(),
  /** Null when the organization does not ask for it (PLAN v4). */
  locality: z.string().nullable(),
  district: z.string().nullable(),
  districtStatus: DistrictStatusSchema,
  /** The member opted out of the organization's emails (profile toggle, footer link). */
  emailOptOut: z.boolean(),
})
export type MemberMe = z.infer<typeof MemberMeSchema>
export const MemberMeResponseSchema = z.object({ member: MemberMeSchema })
export type MemberMeResponse = z.infer<typeof MemberMeResponseSchema>

/* PATCH /api/o/:orgSlug/me/locality (cookie) → MemberMeResponse (re-mapped; unresolved ⇒ review flag). */
export const UpdateLocalityRequestSchema = z.object({ locality: z.string().trim().min(1).max(200) })
export type UpdateLocalityRequest = z.infer<typeof UpdateLocalityRequestSchema>

/* ================================================================== privacy: delete my data */

/**
 * POST /api/o/:orgSlug/me/erase → OkResponse. Irreversible: the membership ends and the member's personal data is
 * deleted (here and at Didit). Confirmed twice: the typed word and the password (its auth verifier, as at login).
 */
export const EraseMyDataRequestSchema = z.object({
  confirm: z.literal('DELETE'),
  authVerifier: Hex32Schema,
})
export type EraseMyDataRequest = z.infer<typeof EraseMyDataRequestSchema>

/* ================================================================== recovery (12 words) */

/* POST /api/o/:orgSlug/recover/challenge → RecoverChallengeResponse (always 200 to avoid account enumeration). */
export const RecoverChallengeRequestSchema = z.object({ email: EmailSchema })
export type RecoverChallengeRequest = z.infer<typeof RecoverChallengeRequestSchema>
export const RecoverChallengeResponseSchema = z.object({
  /** Message to sign with `wallet.signMessage(challenge)` (EIP-191) using the wallet derived from the 12 words. */
  challenge: z.string(),
  expiresAt: IsoDateTimeSchema,
})
export type RecoverChallengeResponse = z.infer<typeof RecoverChallengeResponseSchema>

/*
 * POST /api/o/:orgSlug/recover/complete → MemberMeResponse + cookie.
 * Server checks verifyMessage(challenge, signature) == address == member.wallet_address, then stores the new
 * keystore + verifier. 400 challenge_expired | 401 invalid_signature.
 */
export const RecoverCompleteRequestSchema = z.object({
  email: EmailSchema,
  challenge: z.string().min(16),
  signature: HexSchema,
  address: EthAddressSchema,
  keystore: KeystoreJsonSchema,
  authVerifier: Hex32Schema,
})
export type RecoverCompleteRequest = z.infer<typeof RecoverCompleteRequestSchema>

/* ================================================================== member home: my votings */

export const MemberElectionItemSchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  title: z.string(),
  status: ElectionStatusSchema,
  schedule: ScheduleSchema,
  /** Earliest process start / latest process end once processes exist (else from the schedule, may be null). */
  startDate: IsoDateTimeSchema.nullable(),
  endDate: IsoDateTimeSchema.nullable(),
  questionsCount: z.number().int().min(0),
  /** Live eligibility (memberEligibility): district filter, age at the voting start, status. */
  eligible: z.boolean(),
  ineligibleReason: IneligibilityReasonSchema.nullable(),
  /** Questions this member has a recorded ballot for. */
  votedQuestions: z.number().int().min(0),
  residenceDeclarationDays: z.number().int().nullable(),
  hasResults: z.boolean(),
  testMode: z.boolean(),
})
export type MemberElectionItem = z.infer<typeof MemberElectionItemSchema>

/*
 * GET /api/o/:orgSlug/me/elections (cookie) → MyElectionsResponse
 * open: status open (and closing); upcoming: published / starting (scheduled or waiting for Start); past: ended,
 * results, canceled. Drafts never appear. Each list sorted by start (open/upcoming) or end (past, newest first).
 */
export const MyElectionsResponseSchema = z.object({
  open: z.array(MemberElectionItemSchema),
  upcoming: z.array(MemberElectionItemSchema),
  past: z.array(MemberElectionItemSchema),
})
export type MyElectionsResponse = z.infer<typeof MyElectionsResponseSchema>

/* ================================================================== voting page */

/*
 * GET /api/o/:orgSlug/elections/:electionSlug/me (cookie) → MemberElectionStatusResponse
 * The member's view of one voting: eligibility (live), residence declaration made for it (M2) and recorded ballots.
 * The voting itself (questions, processes, results) comes from the public route publicElection.
 */
export const MemberElectionStatusResponseSchema = z.object({
  electionId: UuidSchema,
  eligible: z.boolean(),
  ineligibleReason: IneligibilityReasonSchema.nullable(),
  /** The member's latest residence declaration for THIS voting (null when never made or not asked). */
  residenceDeclaration: z.object({ days: z.number().int().min(1), declaredAt: IsoDateTimeSchema }).nullable(),
  votes: z.array(VoteRecordSchema),
})
export type MemberElectionStatusResponse = z.infer<typeof MemberElectionStatusResponseSchema>

/*
 * POST /api/o/:orgSlug/elections/:electionSlug/csp { processId, address, residenceDeclared } (cookie)
 *   → CspAttestationResponse
 * A POST (security round S1, audit M6): it records the residence declaration, and a cookie-authenticated GET is sent
 * on cross-site top-level navigation (SameSite=Lax), so a crafted link could have declared on the member's behalf.
 * Signed only if: memberEligibility() passes (active, not removed, district ∈ eligibleDistricts when set, age at
 * the voting start in range: else 403 not_eligible with details.reason), address == member wallet, processId ∈ this
 * voting's questions and that process is open (409 voting_closed), and — when the voting has
 * residenceDeclarationDays — `residenceDeclared=true` (else 403 residence_declaration_required). The declaration is
 * stored per (member, voting) (M2). Index = the member's census slot (stable per person in the organization, M1),
 * weight 1. Convert for the SDK censusProvider: { ...r, weight: BigInt(r.weight), index: BigInt(r.index) }.
 */
export const CspRequestSchema = z.object({
  processId: ProcessIdSchema,
  address: EthAddressSchema,
  /** The member ticked the residence declaration for this ballot. */
  residenceDeclared: z.boolean().optional(),
})
export type CspRequest = z.infer<typeof CspRequestSchema>
export const CspAttestationResponseSchema = z.object({
  address: EthAddressSchema,
  weight: BigIntStringSchema,
  index: BigIntStringSchema,
  r: HexSchema,
  s: HexSchema,
  recid: z.union([z.literal(0), z.literal(1)]),
})
export type CspAttestationResponse = z.infer<typeof CspAttestationResponseSchema>

/*
 * POST /api/o/:orgSlug/elections/:electionSlug/votes (cookie) → RecordVoteResponse
 * Upsert on (member, question): call after submitVote() resolves, and again when the polled status changes.
 */
export const RecordVoteRequestSchema = z.object({
  questionId: UuidSchema,
  processId: ProcessIdSchema,
  voteId: z.string().min(1).max(200),
  node: z.string().min(1).max(500),
  status: VoteStatusSchema,
  gps: GpsReadingSchema.optional(),
})
export type RecordVoteRequest = z.infer<typeof RecordVoteRequestSchema>
export const RecordVoteResponseSchema = z.object({ vote: VoteRecordSchema })
export type RecordVoteResponse = z.infer<typeof RecordVoteResponseSchema>

/* GET /api/o/:orgSlug/elections/:electionSlug/votes (cookie) → VotesResponse (this member, this voting) */
export const VotesResponseSchema = z.object({ votes: z.array(VoteRecordSchema) })
export type VotesResponse = z.infer<typeof VotesResponseSchema>
